/**
 * WritersDashboardConfig - Configuration for WritersDashboardApp_v2
 * Dashboard for self-published authors to organize their writing process
 */

import { AppConfig } from '../../../services/baseApplication/types';

export const STEPS_VERSION = 24;

export type StepActionType =
  | 'create_note'
  | 'create_canvas'
  | 'create_doc'
  | 'open_drive'
  | 'launch_prompt'
  | 'upload_files'
  | 'start_manuscript_review'
  | 'apply_edits'
  | 'create_book'
  | 'configure_style_guide';

export interface StepAction {
  type: StepActionType;
  label: string;
  icon: string;
  color: string;
  promptText?: string;
  entityTitle?: string;
  accept?: string;
  addToChat?: boolean;
  requiresReportPicker?: boolean;
}

export interface WritingStep {
  id: string;
  name: string;
  instructions: string;
  actions: StepAction[];
  order: number;
  completed: boolean;
  optional?: boolean;
}

const IDEA_GENERATOR_PROMPT = `I need help generating book ideas. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read any existing content in this space — specifically the Brainstorm Notes note, the Brainstorm Canvas, and any uploaded or imported files.

**Step 2 — Ask one question, then wait**
If the space has content (notes, ideas, or uploaded files): briefly summarise what you found, then ask:
**"Want me to build on what's here, or explore something completely different?"**

If the space is empty or has nothing useful: offer 3 short seed ideas across different genres as examples, then ask:
**"What kind of book are you thinking about?"**

Stop here and wait for a response before continuing.

**Step 3 — Generate 5 ideas**
Based on the response, generate 5 distinct premises. For each one include:
- A one-sentence hook
- The core conflict or central question
- The kind of protagonist who would carry it
- What makes it feel specific and worth writing

Be bold — no safe or predictable ideas.

**Step 4 — Save to notes**
Use note_write to append the ideas to the Brainstorm Notes note. If it doesn't exist yet, use entity_create to create it first. Format with a heading and numbered list.

**Step 5 — Continue the conversation**
Ask which ideas resonate, or invite me to share my own direction.`;

const BIBLE_CANVAS_PROMPT = `Help me build a visual character and world map on the canvas. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read the World & Character Bible doc, the Brainstorm Notes note, and the Brainstorm Canvas in this space. Extract every named character, location, faction, and relationship you can find.

**Step 2 — Ask one question, then wait**
Briefly summarise what you found (characters, locations, relationships). Then ask:
**"Should the map focus on characters, the world, or both equally?"**

Stop here and wait for a response before continuing.

**Step 3 — Write to the canvas**
Use canvas_write to populate the Character & World Map canvas based on the response:

- **Characters** — one node per major character. Label: "[Name] — [one-line role]". Include a note on their defining trait and internal conflict.
- **World** — one node per key location, faction, or system. Label: "[Name] — [one-line description]".
- **Connections** — labelled edges between nodes showing relationships (ally, rival, family, love interest, controls, wants something from, etc.).

Keep labels short. Arrange characters on one side and world elements on the other.

**Step 4 — Confirm**
Briefly summarise what was added and ask if anything needs adjusting.`;

const PORTRAITS_PROMPT = `Generate visual portraits for the key characters and locations in my book, assembled into a presentation with one portrait per slide. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read the World & Character Bible doc, the Brainstorm Notes note, and the Brainstorm Canvas in this space. Extract each named character's appearance, personality, and role — and each key location's mood, scale, and defining features.

**Step 2 — Ask one question, then wait**
List the characters and locations you plan to generate portraits for. Then ask:
**"Any preferences on visual style — realistic, painterly, cinematic? Or should I go with what fits the story?"**

Stop here and wait for a response before continuing.

**Step 3 — Create the presentation**
Use entity_create to create a new Presentation named "Character & Location Portraits" in this space.

**Step 4 — Generate and add slides one at a time**
For each character or location (characters first, then locations):
1. State the name and what you are about to generate
2. Generate the portrait image in the chat:
   - Characters: portrait-style, capturing appearance, age, and emotional quality
   - Locations: atmospheric illustration capturing mood, scale, and feeling
3. Use slide_write to add a new slide with the name as the slide title
4. Pause for feedback before moving to the next one

**Step 5 — Confirm**
After all portraits are complete, confirm how many slides were added and ask if any need regenerating.`;

const RESEARCH_PROMPT = `Help me research my book. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read the Brainstorm Notes note, the Brainstorm Canvas, and any uploaded or imported files in this space. Understand the story's world, themes, setting, and time period before proceeding.

**Step 2 — Ask one question, then wait**
Briefly summarise what you found about the story. Then ask:
**"What are you most uncertain about — the setting, technical details, cultural context, or something else?"**

Stop here and wait for a response before continuing.

**Step 3 — Plan the research**
Based on the response, identify the topics that need research. Group them into 2–4 clusters by theme (e.g. "Setting & Period", "Technical Accuracy", "Cultural Context"). Briefly state the plan before starting.

**Step 4 — Research each topic**
For each topic, use web_search to find accurate, relevant information. Prioritise the most story-critical topics first.

**Step 5 — Create one doc per cluster**
For each cluster:
1. Use entity_create to create a new Doc named "Research — [Cluster Name]"
2. Use doc_write to write the findings into that doc, with a section per topic containing:
   - Why it matters to the story
   - Key facts and details to know
   - Recommended sources or reference types to explore further

Keep each doc focused on its cluster — do not combine all topics into one document.

**Step 6 — Confirm**
List the docs created and summarise what was covered in each. Flag any topics where reliable information was hard to find.`;

const OUTLINE_PROMPT = `Help me build a structured outline for my book. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read all relevant content in this space: the Brainstorm Notes note, the Brainstorm Canvas, the World & Character Bible doc, and any Research Notes. Extract the story's premise, major characters, world, and themes.

**Step 2 — Ask one question, then wait**
Briefly summarise the story premise as you understand it. Then ask:
**"Do you have a structure in mind (3-act, hero's journey, Save the Cat, etc.), or should I choose one?"**

Stop here and wait for a response before continuing.

**Step 3 — Build the outline**
Based on the response, write a complete outline covering:
- **Act/phase breakdown** — where each structural phase begins and its dramatic purpose (inciting incident, midpoint, climax, resolution, etc.)
- **Chapter-by-chapter breakdown** — a 1–2 sentence description of what happens in each chapter, including scene, conflict, and outcome
- **Character arcs** — how each major character changes from the first page to the last
- **Pacing notes** — where tension escalates, where it releases, and where the reader needs space to breathe

**Step 4 — Write to the Outline doc**
Use doc_write to write the complete outline directly into the Outline doc in this space. Use clear headings for each section.

**Step 5 — Confirm**
Briefly summarise the structure and ask if any section needs reworking.`;

const TIMELINE_PROMPT = `Build a visual story timeline on the canvas. Here is how to proceed:

**Step 1 — Read the outline**
Use entity_read to read the Outline doc in this space. Extract all story beats, acts, chapters, and character moments in story order.

**Step 2 — Ask one question, then wait**
Briefly state how many beats/chapters you found in the outline. Then ask:
**"Should I map events in chronological story order, or against the structural beats (acts, turning points)?"**

Stop here and wait for a response before continuing.

**Step 3 — Write to the canvas**
Use canvas_write to populate the Story Structure canvas based on the response. Include:

- **Act markers** — label the start of each structural phase (e.g. "Act 1 — Setup", "Inciting Incident", "Midpoint", "Act 3 — Resolution")
- **Story beats** — one node per major event or chapter. Label format: "[Chapter/Beat title] — [5–8 word description]"
- **Character flags** — note which character(s) are central to each beat
- **Tone markers** — tag the emotional register of each beat (e.g. "tension rising", "relief", "revelation", "climax")

Arrange nodes left-to-right. Use act markers as visual anchors.

**Step 4 — Confirm**
Describe the timeline layout and ask if anything needs adjusting.`;

const DRAFT_CHAPTER_1_PROMPT = `Write Chapter 1 of my book in full. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read the Outline doc, the World & Character Bible, and the Brainstorm Notes note in this space. Extract what happens in Chapter 1, the protagonist's starting situation, voice, and the established POV and tonal register.

**Step 2 — Ask one question, then wait**
Briefly summarise how you understand Chapter 1 should begin. Then ask:
**"Anything specific about Chapter 1 you want me to keep in mind before I start?"**

Stop here and wait for a response before continuing.

**Step 3 — Write Chapter 1 in full**
Write the complete chapter as it would appear in the finished book:
- Full prose, scene by scene, from the first word to the last
- Match the voice, tone, and POV from the outline and any sample material
- Do not summarise, skip scenes, or add authorial commentary — just write
- Do not stop mid-chapter to check in; complete the full draft first

**Step 4 — Write to the First Draft doc**
Use doc_write to insert Chapter 1 into the First Draft doc. Place it under the heading "## Chapter 1".

**Step 5 — Confirm**
Confirm what was added and wait for feedback before moving on to Chapter 2.`;


const KDP_METADATA_PROMPT = `Generate complete KDP (Kindle Direct Publishing) metadata for my book. Here is how to proceed:

**Step 1 — Read context**
Use entity_read to read the manuscript (First Draft doc) and any other docs in this space. Extract the title, genre, themes, plot, protagonist, and tone.

**Step 2 — Ask one question, then wait**
Briefly state the title, genre, and audience you've identified. Then ask:
**"What name should I use for the author bio?"**

Stop here and wait for a response before continuing.

**Step 3 — Plan the metadata**
Before writing, state:
- Your proposed title and subtitle (with brief reasoning)
- The 2 BISAC categories you will use and why

**Step 4 — Generate each metadata field**
Produce all of the following:

1. **Book Title** — confirm or refine the best title for Amazon discoverability
2. **Subtitle** — a compelling subtitle that adds search keywords (optional but recommended for non-fiction and genre fiction)
3. **Book Description** — 250–400 words (KDP allows up to 4000 characters), written in third person, optimised for Amazon. Use HTML tags that KDP accepts: \`<b>\`, \`<i>\`, \`<em>\`, \`<strong>\`, \`<p>\`, \`<br>\`, \`<h4>\`, \`<ul>\`, \`<ol>\`, \`<li>\`. Open with a hook, build tension, end with a call to read. Keep paragraphs short and scannable.
4. **Keywords** — exactly 7 keyword phrases (each up to 50 characters), chosen for Amazon search relevance. Avoid repeating words already in the title.
5. **BISAC Categories** — the 2 most accurate Amazon browse categories. Use the full BISAC path (e.g. "FICTION / Thrillers / Psychological").
6. **Author Bio** — 50–150 words, professional tone, written in third person. Use the name provided.
7. **Age & Grade Range** — include only if this is a children's or YA book

**Step 5 — Write to the KDP Metadata doc**
Use doc_write to write all metadata fields into the KDP Metadata doc in this space. Use a clear heading for each field so each section can be copied directly into the KDP dashboard.

**Step 6 — Confirm**
After writing, flag any fields that may need my review or personalisation before submitting.`;

export const APPLY_EDITS_PROMPT = `Work through the Beta Read report and apply edits to the manuscript. Here is how to proceed:

**Step 1 — Read both documents**
Use entity_read to read:
1. The Beta Read report — extract every editorial suggestion
2. The manuscript (First Draft doc) — so you know exactly where each issue appears

**Step 2 — Ask one question, then wait**
Briefly summarise the key themes from the Beta Read report (e.g. structural issues, pacing, character arcs, prose). Then ask:
**"Any types of edits you want to focus on or skip?"**

Stop here and wait for a response before continuing.

**Step 3 — Triage**
Based on the response, list the issues to address in priority order:
- Structural and plot issues first
- Character and arc issues second
- Scene-level pacing and consistency issues third
- Line-level prose edits last

Present this triage list and wait for confirmation before proceeding.

**Step 4 — Work through edits one by one**
For each issue, in priority order:
1. Identify the exact passage in the manuscript that needs changing
2. Tell me what the problem is and show me your proposed rewrite (quote the original and the proposed change)
3. Wait for me to accept, reject, or ask for an alternative
4. If I accept: use doc_write to apply the change to the manuscript, then confirm it was applied
5. If I reject or request an alternative: do not touch the manuscript — propose a revised approach instead

Do not batch edits. One change at a time, with confirmation in between.

**Step 5 — Track progress**
After each accepted edit, note how many issues remain. When all edits are complete, give a brief summary of what was changed.`;

export const DEFAULT_WRITING_STEPS: Omit<WritingStep, 'completed'>[] = [
  {
    id: 'step_style_guide',
    name: 'Writing Style Guide',
    instructions: 'Define the AI writing style for your book: POV, tense, genre, tone, and prose style.',
    actions: [
      { type: 'configure_style_guide', label: 'Configure Style', icon: 'Palette', color: 'blue' }
    ],
    order: 1,
    optional: true
  },
  {
    id: 'step_import',
    name: 'Import',
    instructions: 'Import your existing manuscript, notes, and research.',
    actions: [
      { type: 'upload_files', label: 'Upload Files', icon: 'UploadSimple', color: 'indigo', accept: '.doc,.docx,.pdf,.txt,.md', addToChat: false }
    ],
    order: 2
  },
  {
    id: 'step_brainstorm',
    name: 'Brainstorm',
    instructions: 'Capture ideas freely. Use notes for raw text, canvas for mind maps, or upload photos of physical notes to bring them into River.',
    actions: [
      { type: 'create_note', label: 'New Note', icon: 'Note', color: 'blue', entityTitle: 'Brainstorm Notes' },
      { type: 'create_canvas', label: 'New Canvas', icon: 'ChartLine', color: 'purple', entityTitle: 'Brainstorm Canvas' },
      { type: 'upload_files', label: 'Upload Images', icon: 'Images', color: 'indigo', accept: 'image/*', addToChat: true },
      { type: 'launch_prompt', label: 'Generate Ideas', icon: 'Sparkle', color: 'amber', promptText: IDEA_GENERATOR_PROMPT }
    ],
    order: 3
  },
  {
    id: 'step_research',
    name: 'Research',
    instructions: 'Gather references, articles, and source material to ground your book. Browse the web, upload files, and use AI to identify what you need to get right.',
    actions: [
      { type: 'open_drive', label: 'Open Drive', icon: 'FolderOpen', color: 'orange' },
      { type: 'launch_prompt', label: 'Research with AI', icon: 'Sparkle', color: 'amber', promptText: RESEARCH_PROMPT }
    ],
    order: 4
  },
  {
    id: 'step_bible',
    name: 'World & Character Bible',
    instructions: 'Build your single source of truth for the book: characters, world rules, lore, and continuity.',
    actions: [
      { type: 'create_doc', label: 'New Doc', icon: 'FileText', color: 'green', entityTitle: 'World & Character Bible' },
      { type: 'create_canvas', label: 'New Canvas', icon: 'ChartLine', color: 'purple', entityTitle: 'Character & World Map' },
      { type: 'launch_prompt', label: 'Map with AI', icon: 'Sparkle', color: 'amber', promptText: BIBLE_CANVAS_PROMPT },
      { type: 'launch_prompt', label: 'Generate Portraits', icon: 'Sparkle', color: 'amber', promptText: PORTRAITS_PROMPT }
    ],
    order: 5
  },
  {
    id: 'step_outline',
    name: 'Outline',
    instructions: 'Plan your story structure before writing: chapters, acts, and scenes.',
    actions: [
      { type: 'create_doc', label: 'New Doc', icon: 'FileText', color: 'green', entityTitle: 'Outline' },
      { type: 'create_canvas', label: 'New Canvas', icon: 'ChartLine', color: 'purple', entityTitle: 'Story Structure' },
      { type: 'launch_prompt', label: 'Outline with AI', icon: 'Sparkle', color: 'amber', promptText: OUTLINE_PROMPT },
      { type: 'launch_prompt', label: 'Build Timeline', icon: 'Sparkle', color: 'amber', promptText: TIMELINE_PROMPT }
    ],
    order: 6
  },
  {
    id: 'step_draft',
    name: 'First Draft',
    instructions: 'Write your book from start to finish. Keep moving forward. Don\'t stop to edit. A dedicated doc keeps your draft clean and focused.',
    actions: [
      { type: 'create_doc', label: 'New Doc', icon: 'FileText', color: 'green', entityTitle: 'First Draft' },
      { type: 'launch_prompt', label: 'Draft Chapter 1', icon: 'Sparkle', color: 'amber', promptText: DRAFT_CHAPTER_1_PROMPT }
    ],
    order: 7
  },
  {
    id: 'step_ai_review',
    name: 'AI Review',
    instructions: 'Run an editorial pass on your draft.',
    actions: [
      { type: 'start_manuscript_review', label: 'Beta Read', icon: 'Sparkle', color: 'amber' }
    ],
    order: 8
  },
  {
    id: 'step_apply_edits',
    name: 'Apply Edits',
    instructions: 'Work through AI suggestions one by one, accepting or rejecting each change before it goes into the manuscript.',
    actions: [
      { type: 'apply_edits', label: 'Apply with AI', icon: 'Sparkle', color: 'amber' }
    ],
    order: 9
  },
  {
    id: 'step_build',
    name: 'Build the Book',
    instructions: 'Create your book project, format your manuscript, and assemble it ready for publishing.',
    actions: [
      { type: 'create_book', label: 'New Book', icon: 'BookOpen', color: 'rose', entityTitle: 'My Book' }
    ],
    order: 10
  },
  {
    id: 'step_kdp_metadata',
    name: 'KDP Metadata',
    instructions: 'Generate your Amazon listing: title, description, keywords, and categories, ready to paste into KDP.',
    actions: [
      { type: 'create_doc', label: 'New Doc', icon: 'FileText', color: 'green', entityTitle: 'KDP Metadata' },
      { type: 'launch_prompt', label: 'Generate with AI', icon: 'Sparkle', color: 'amber', promptText: KDP_METADATA_PROMPT }
    ],
    order: 11
  }
];

export interface DashboardConfig {
  isConfigured: boolean;
  goalsHidden: boolean;
  wordCountGoal: number;
  dailyWritingGoal: number;
  targetCompletionDate: string;
}

export interface DashboardMetrics {
  currentWordCount: number;
  todaysWordCount: number;
  lastUpdated: number;
  lastDayTracked?: string;
}

export const WRITERS_DASHBOARD_CONFIG: AppConfig = {
  appId: 'writers_dashboard_v2',
  appName: 'Writing Hub',
  containerType: 'note',
  layoutMode: 'flow',
  supportedClasses: {
    'dashboard-config': {
      displayName: 'Dashboard Config',
      description: 'Configuration data for dashboard',
      category: 'metadata'
    },
    'dashboard-step': {
      displayName: 'Writing Step',
      description: 'A step in the writing process',
      category: 'task'
    }
  },
  supportedPositionTypes: ['flow'],
  supportsDragDrop: false,
  supportsResize: false,
  supportsRotation: false,
  defaultContainer: {
    width: undefined,
    height: undefined,
    infinite: false
  },
  editorSelector: '.writers-dashboard-app-v2'
};
